/* Push notification subscription helpers for Discoveur.
 * - subscribeUser(): asks permission, registers a PushSubscription with
 *   the SW, and POSTs it to /api/push/subscribe.
 * - unsubscribeUser(): unsubscribes locally + notifies backend.
 * - refreshAppBadge(): pulls the unread count from /api/push/status and
 *   calls navigator.setAppBadge() (WhatsApp-like).
 */
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

export function pushSupported() {
    return typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window;
}

export async function getActiveSubscription() {
    if (!pushSupported()) return null;
    const reg = await navigator.serviceWorker.ready;
    return reg.pushManager.getSubscription();
}

export async function subscribeUser() {
    if (!pushSupported()) return { ok: false, reason: "unsupported" };

    if (Notification.permission === "denied") {
        return { ok: false, reason: "denied" };
    }
    if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") return { ok: false, reason: "denied" };
    }

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();

    if (!sub) {
        const { data } = await api.get("/push/vapid-public-key");
        if (!data?.configured || !data.public_key) {
            return { ok: false, reason: "vapid_not_configured" };
        }
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
    }

    const json = sub.toJSON();
    await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        expirationTime: json.expirationTime || null,
    });
    return { ok: true };
}

export async function unsubscribeUser() {
    if (!pushSupported()) return { ok: false };
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };
    try {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
    } catch (_) { /* ignore */ }
    await sub.unsubscribe();
    return { ok: true };
}

export async function refreshAppBadge() {
    try {
        const { data } = await api.get("/push/status");
        const n = Number(data?.badge || 0);
        if ("setAppBadge" in navigator) {
            if (n > 0) await navigator.setAppBadge(n);
            else if ("clearAppBadge" in navigator) await navigator.clearAppBadge();
        }
        return n;
    } catch (_) {
        return 0;
    }
}

export async function sendTestPush() {
    try {
        await api.post("/push/test", {});
        return { ok: true };
    } catch (e) {
        return { ok: false, error: e };
    }
}
